import {
  AdapterMessage,
  AdapterReply,
  MinimalProviderConfig,
  ModelInfo,
  OnChunk,
  TokenUsage,
  createProviderApiError,
  normalizeEndpoint,
  recordLiveContextTokens,
  safeErrorDetail,
  wrapNetworkError,
} from "./types";
import { httpRequest, httpStream, HttpResponse, HttpStreamResponse } from "../http";

// Gemini's REST API shape is different enough from OpenAI's to need its own translator:
// messages become "contents", roles are user/model, and the API key is a query parameter
// rather than an Authorization header.

// Same per-chunk shape as the old single non-streaming response body — streamGenerateContent
// just sends this same shape repeatedly, each chunk carrying the next slice of candidate text.
interface GeminiStreamChunk {
  candidates?: { content?: { parts?: { text?: unknown }[] }; finishReason?: string }[];
  promptFeedback?: { blockReason?: string };
  usageMetadata?: { promptTokenCount?: unknown; candidatesTokenCount?: unknown };
}

// `signal` is the Stop button / typing-bubble click (chat.ts) — see openaiCompatible.ts's
// sendMessage for the same comment, applies identically here. `onChunk` (optional) fires with
// each incremental slice of reply text as it streams in — see adapters/types.ts's OnChunk.
//
// Streams via `:streamGenerateContent?alt=sse` rather than `:generateContent` — `alt=sse` is
// required, not optional: without it this endpoint returns a single growing JSON array instead
// of real SSE `data:` frames, which httpStream's frame parser wouldn't understand.
export async function sendMessage(
  config: MinimalProviderConfig,
  messages: AdapterMessage[],
  signal?: AbortSignal,
  onChunk?: OnChunk
): Promise<AdapterReply> {
  const model = config.model.startsWith("models/") ? config.model : `models/${config.model}`;
  const url = `${normalizeEndpoint(config.endpoint)}/${model}:streamGenerateContent?alt=sse&key=${encodeURIComponent(config.apiKey)}`;

  const contents = messages.map((m) => ({
    role: m.role === "assistant" ? "model" : "user",
    parts: [{ text: m.content }],
  }));

  let response: HttpStreamResponse;
  try {
    response = await httpStream(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify({
        contents,
        ...(config.temperature !== undefined
          ? { generationConfig: { temperature: config.temperature } }
          : {}),
        // Gemini keeps system instructions in their own top-level field, separate from the
        // conversation turns in "contents" — not just another message in the history.
        ...(config.systemPrompt
          ? { systemInstruction: { parts: [{ text: config.systemPrompt }] } }
          : {}),
      }),
      signal,
    });
  } catch (err) {
    wrapNetworkError(config.name, err);
  }

  if (!response.ok) {
    throw createProviderApiError(
      `${config.name} returned an error (${response.status}): ${await safeErrorDetail(response)}`
    );
  }

  let text = "";
  let blockReason: string | undefined;
  let finishReason: string | undefined;
  let usage: TokenUsage | undefined;
  try {
    for await (const frame of response.events()) {
      let parsed: GeminiStreamChunk;
      try {
        parsed = JSON.parse(frame.data);
      } catch {
        // A malformed/unexpected frame shouldn't fail an otherwise-good reply — skip it rather
        // than aborting text already received.
        continue;
      }
      const candidate = parsed.candidates?.[0];
      const parts = candidate?.content?.parts;
      if (Array.isArray(parts)) {
        for (const p of parts) {
          if (typeof p?.text === "string" && p.text) {
            text += p.text;
            onChunk?.(p.text);
          }
        }
      }
      // finishReason/blockReason are typically only present on the final chunk (or, for a
      // safety block, may be the *only* chunk, with no candidates/text at all).
      if (candidate?.finishReason) {
        finishReason = candidate.finishReason;
      }
      if (parsed.promptFeedback?.blockReason) {
        blockReason = parsed.promptFeedback.blockReason;
      }
      if (
        typeof parsed.usageMetadata?.promptTokenCount === "number" &&
        typeof parsed.usageMetadata?.candidatesTokenCount === "number"
      ) {
        usage = {
          inputTokens: parsed.usageMetadata.promptTokenCount,
          outputTokens: parsed.usageMetadata.candidatesTokenCount,
        };
      }
    }
  } catch (err) {
    wrapNetworkError(config.name, err);
  }

  if (!text) {
    if (blockReason) {
      throw createProviderApiError(`${config.name} blocked this request (reason: ${blockReason}).`);
    }
    // The prompt itself can be fine while the *generated output* still gets blocked — e.g.
    // finishReason "RECITATION" fires when the reply closely matches known copyrighted text
    // (this is common when working with excerpts from a published book), and "SAFETY" fires
    // when the output (not the input) trips a content filter. Neither sets promptFeedback.
    if (finishReason && finishReason !== "STOP") {
      throw createProviderApiError(describeFinishReason(config.name, finishReason));
    }
    throw createProviderApiError(`${config.name} returned a response in an unexpected shape.`);
  }

  // Some text can still stream in before finishReason trips — e.g. MAX_TOKENS after a long
  // partial reply, or (less commonly) SAFETY/RECITATION partway through. Previously this branch
  // only ever ran when text was empty, so a truncated-but-nonempty reply was accepted silently
  // with no signal to the user. Surfaced as a trailing note instead, mirroring anthropic.ts's
  // own "[Reply cut off...]" note for its equivalent max_tokens case, rather than discarding the
  // real (if incomplete) text via an error.
  if (finishReason && finishReason !== "STOP") {
    return { text: `${text}\n\n[${describeFinishReason(config.name, finishReason)}]`, usage };
  }

  return { text, usage };
}

function describeFinishReason(providerName: string, reason: string): string {
  switch (reason) {
    case "SAFETY":
      return `${providerName} declined to generate a response because its safety filters flagged the content.`;
    case "RECITATION":
      return `${providerName} declined to generate a response because it closely matched known copyrighted material (common with excerpts from a published book) — try rephrasing your request instead of asking it to reproduce or closely follow the passage.`;
    case "MAX_TOKENS":
      return `${providerName}'s response was cut off for being too long. Try asking for a shorter reply, or select a smaller passage.`;
    default:
      return `${providerName} did not generate a response (reason: ${reason}).`;
  }
}

export async function listModels(
  config: Pick<MinimalProviderConfig, "endpoint" | "apiKey">
): Promise<ModelInfo[]> {
  const url = `${normalizeEndpoint(config.endpoint)}/models?key=${encodeURIComponent(config.apiKey)}`;

  let response: HttpResponse;
  try {
    response = await httpRequest(url, {});
  } catch (err) {
    throw createProviderApiError(`Network error fetching model list: ${(err as Error).message}`);
  }

  if (!response.ok) {
    throw createProviderApiError(
      `Could not fetch model list (${response.status}): ${await safeErrorDetail(response)}`
    );
  }

  const data = (await response.json()) as { models?: unknown };
  const list = data?.models;
  if (!Array.isArray(list)) {
    throw createProviderApiError("Model list response was in an unexpected shape.");
  }

  const models = list
    .filter((m: { supportedGenerationMethods?: unknown }) =>
      Array.isArray(m?.supportedGenerationMethods)
        ? m.supportedGenerationMethods.includes("generateContent")
        : true
    )
    // inputTokenLimit is the real context window straight from Gemini's own API — used as the
    // live tier in getDefaultMaxContextTokens (storage.ts) instead of a guess.
    .map((m: { name?: unknown; inputTokenLimit?: unknown }): ModelInfo | null =>
      typeof m?.name === "string"
        ? {
            id: m.name.replace(/^models\//, ""),
            contextTokens: typeof m.inputTokenLimit === "number" ? m.inputTokenLimit : undefined,
          }
        : null
    )
    .filter((info: ModelInfo | null): info is ModelInfo => info !== null)
    .sort((a, b) => a.id.localeCompare(b.id));

  // Populates recordLiveContextTokens's shared cache (adapters/types.ts) so storage.ts's
  // getTruncatedHistory can actually use this real number at send time, not just show it in a
  // placeholder — see that cache's own comment for the full picture.
  for (const model of models) {
    recordLiveContextTokens(config.endpoint, model.id, model.contextTokens);
  }

  return models;
}
